/**
 * Formulaire d'adhésion public — Front Uni pour la RDC
 * Multi-étapes, mobile-first, institutionnel premium.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, CheckCircle2, User, MapPin, Sparkles, Send,
  Loader2, AlertCircle, Mail, Phone, Briefcase, Globe, Building,
  Heart, Copy as CopyIcon, Check,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Logo from "../components/Logo";

const ENGAGEMENT_LABELS = {
  adherent: { label: "Adhérent simple", desc: "Je souhaite être membre et soutenir le mouvement." },
  benevole: { label: "Bénévole", desc: "Je souhaite donner de mon temps pour des actions ponctuelles." },
  militant: { label: "Militant", desc: "Je m'engage activement dans le plaidoyer et les mobilisations." },
  sympathisant: { label: "Sympathisant", desc: "Je soutiens les valeurs et actions du mouvement." },
};

const SOURCES = [
  "Réseaux sociaux (Facebook, WhatsApp, X…)", "Un(e) ami(e) / membre existant",
  "Un événement ou conférence", "La presse / les médias",
  "Le site web frontunirdc.be", "Un moteur de recherche", "Autre",
];

const INITIAL = {
  first_name: "", last_name: "", email: "", phone: "",
  country: "", city: "", profession: "",
  engagement_type: "adherent",
  motivation: "", source: "",
  honeypot: "", // anti-bot
};

const STEPS = [
  { key: "identity", label: "Identité", Icon: User },
  { key: "location", label: "Coordonnées", Icon: MapPin },
  { key: "engagement", label: "Engagement", Icon: Heart },
  { key: "review", label: "Confirmation", Icon: CheckCircle2 },
];

export default function AdhesionPage() {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const validate = () => {
    if (step === 0) {
      if (!f.first_name.trim() || !f.last_name.trim()) return "Merci d'indiquer votre nom et prénom";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) return "Email invalide";
      if (f.phone.replace(/\D/g, "").length < 6) return "Téléphone invalide";
    }
    if (step === 1) {
      if (!f.country.trim() || !f.city.trim()) return "Merci d'indiquer votre pays et votre ville";
      if (!f.profession.trim()) return "Merci d'indiquer votre profession ou fonction";
    }
    if (step === 2) {
      if (f.motivation.trim().length < 10) return "Merci de préciser votre motivation (10 caractères minimum)";
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) return toast.error(err);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => { setStep((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSubmitting(true); setError(null);
    try {
      await api.post("/adhesions", f);
      setSuccess(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Une erreur est survenue. Merci de réessayer.";
      setError(msg); toast.error(msg);
    } finally { setSubmitting(false); }
  };

  if (success) return <SuccessPage data={f} />;

  return (
    <div className="min-h-screen bg-bone" data-testid="adhesion-page">
      {/* Bandeau institutionnel */}
      <section className="bg-ink text-white grain relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(212,175,55,0.14),transparent_55%)]" />
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 pt-16 pb-14 lg:pt-20 lg:pb-16 text-center">
          <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-3 justify-center">
            <span className="w-8 h-px bg-gold" /> Adhésion officielle
          </div>
          <h1 className="font-serif mt-6 text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">
            Rejoindre <span className="text-gold italic font-light">le Front Uni.</span>
          </h1>
          <p className="mt-6 text-white/80 text-lg max-w-2xl mx-auto">
            Une voix pour la République démocratique du Congo. Complétez votre demande d'adhésion —
            vous recevrez votre numéro officiel après validation.
          </p>
        </div>
      </section>

      {/* Formulaire */}
      <section className="max-w-3xl mx-auto px-6 lg:px-8 -mt-8 relative z-10 pb-16">
        <div className="bg-white shadow-2xl rounded-sm overflow-hidden">
          {/* Progress bar */}
          <div className="border-b border-border">
            <div className="grid grid-cols-4">
              {STEPS.map((s, i) => (
                <div key={s.key}
                     className={`p-4 text-center transition-all ${i === step ? "bg-ink text-white" : i < step ? "bg-bone text-ink" : "bg-white text-foreground/40"}`}
                     data-testid={`step-${s.key}`}>
                  <div className="flex items-center justify-center gap-2">
                    {i < step ? <CheckCircle2 className="w-4 h-4 text-gold" /> : <s.Icon className="w-4 h-4" />}
                    <span className="hidden sm:inline text-[0.7rem] uppercase tracking-widest font-semibold">{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="h-0.5 bg-border relative">
              <div className="absolute top-0 left-0 h-full bg-gold transition-all duration-500"
                   style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
            </div>
          </div>

          {/* Champs par étape */}
          <div className="p-6 sm:p-10 min-h-[400px]">
            {step === 0 && <StepIdentity f={f} set={set} />}
            {step === 1 && <StepLocation f={f} set={set} />}
            {step === 2 && <StepEngagement f={f} set={set} />}
            {step === 3 && <StepReview f={f} />}

            {/* Honeypot invisible */}
            <input tabIndex={-1} autoComplete="off" value={f.honeypot} onChange={(e) => set("honeypot", e.target.value)}
                   className="hidden" aria-hidden="true" />

            {error && (
              <div className="mt-5 p-3 bg-crimson/10 border border-crimson/30 rounded-sm flex items-start gap-2 text-sm text-crimson">
                <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 sm:px-10 py-5 border-t border-border flex items-center justify-between bg-bone/50">
            <button onClick={prev} disabled={step === 0} className="btn-outline text-sm disabled:opacity-30 disabled:cursor-not-allowed" data-testid="adh-prev">
              <ArrowLeft className="w-4 h-4" /> Précédent
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} className="btn-primary text-sm" data-testid="adh-next">
                Continuer <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} className="btn-gold text-sm" data-testid="adh-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Envoyer ma demande
              </button>
            )}
          </div>
        </div>

        {/* Message institutionnel */}
        <div className="mt-8 max-w-2xl mx-auto text-center text-sm text-foreground/60">
          <p>Vos données sont confidentielles et utilisées uniquement pour la gestion de votre adhésion.
             Vous recevrez un email de confirmation avec votre <strong className="text-ink">numéro officiel FURDC-XXXXX</strong> après validation par notre équipe.</p>
        </div>
      </section>
    </div>
  );
}

/* ============ ÉTAPES ============ */

function StepIdentity({ f, set }) {
  return (
    <div className="animate-fade-up">
      <div className="eyebrow text-gold-dark">Étape 1 · Qui êtes-vous ?</div>
      <h2 className="font-serif text-2xl sm:text-3xl mt-2 leading-tight text-ink">Vos coordonnées personnelles</h2>
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <Field label="Prénom *" icon={User}>
          <input value={f.first_name} onChange={(e) => set("first_name", e.target.value)}
                 className="inp" placeholder="Prénom" data-testid="adh-first-name" />
        </Field>
        <Field label="Nom *" icon={User}>
          <input value={f.last_name} onChange={(e) => set("last_name", e.target.value)}
                 className="inp" placeholder="Nom de famille" data-testid="adh-last-name" />
        </Field>
        <Field label="Email *" icon={Mail}>
          <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)}
                 className="inp" placeholder="vous@exemple.com" data-testid="adh-email" autoComplete="email" />
        </Field>
        <Field label="Téléphone *" icon={Phone}>
          <input type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)}
                 className="inp" placeholder="+32 470 12 34 56" data-testid="adh-phone" autoComplete="tel" />
        </Field>
      </div>
    </div>
  );
}

function StepLocation({ f, set }) {
  return (
    <div className="animate-fade-up">
      <div className="eyebrow text-gold-dark">Étape 2 · Où êtes-vous ?</div>
      <h2 className="font-serif text-2xl sm:text-3xl mt-2 leading-tight text-ink">Localisation et profession</h2>
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <Field label="Pays de résidence *" icon={Globe}>
          <input value={f.country} onChange={(e) => set("country", e.target.value)}
                 className="inp" placeholder="Ex : Belgique, France, RDC…" data-testid="adh-country" list="countries" />
          <datalist id="countries">
            {["Belgique","France","Royaume-Uni","Allemagne","Suisse","Pays-Bas","Luxembourg","Italie","Espagne","Portugal",
              "États-Unis","Canada","République démocratique du Congo","Congo (Brazzaville)","Rwanda","Burundi","Ouganda",
              "Afrique du Sud","Sénégal","Côte d'Ivoire","Cameroun","Gabon","Angola","Autre"].map((c) => <option key={c} value={c} />)}
          </datalist>
        </Field>
        <Field label="Ville *" icon={Building}>
          <input value={f.city} onChange={(e) => set("city", e.target.value)}
                 className="inp" placeholder="Ex : Bruxelles, Paris, Kinshasa…" data-testid="adh-city" />
        </Field>
      </div>
      <div className="mt-4">
        <Field label="Profession / Fonction *" icon={Briefcase}>
          <input value={f.profession} onChange={(e) => set("profession", e.target.value)}
                 className="inp" placeholder="Ex : Juriste, Enseignant(e), Étudiant(e), Ingénieur(e)…" data-testid="adh-profession" />
        </Field>
      </div>
    </div>
  );
}

function StepEngagement({ f, set }) {
  return (
    <div className="animate-fade-up">
      <div className="eyebrow text-gold-dark">Étape 3 · Votre engagement</div>
      <h2 className="font-serif text-2xl sm:text-3xl mt-2 leading-tight text-ink">Comment souhaitez-vous nous rejoindre ?</h2>
      <div className="mt-6 space-y-2">
        <div className="text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-2">Type d'engagement *</div>
        {Object.entries(ENGAGEMENT_LABELS).map(([key, v]) => (
          <button key={key} type="button" onClick={() => set("engagement_type", key)}
                  className={`w-full text-left border rounded-sm p-4 transition-all flex items-start gap-3 ${
                    f.engagement_type === key
                      ? "border-gold bg-gold/10 shadow-sm"
                      : "border-border bg-white hover:border-ink/30"
                  }`}
                  data-testid={`adh-engagement-${key}`}>
            <div className={`w-4 h-4 mt-1 rounded-full border-2 flex-shrink-0 ${f.engagement_type === key ? "border-gold bg-gold" : "border-border"}`} />
            <div className="min-w-0">
              <div className="font-semibold text-ink">{v.label}</div>
              <div className="text-sm text-foreground/65 mt-0.5">{v.desc}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-6">
        <Field label="Votre motivation *">
          <textarea rows={4} value={f.motivation} onChange={(e) => set("motivation", e.target.value)}
                    className="inp font-serif" placeholder="Pourquoi souhaitez-vous rejoindre le Front Uni pour la RDC ? Décrivez brièvement ce qui vous anime…"
                    data-testid="adh-motivation" />
          <div className="text-[0.7rem] text-foreground/50 mt-1">{f.motivation.length} caractères — 10 minimum</div>
        </Field>
      </div>

      <div className="mt-4">
        <Field label="Comment avez-vous connu le Front Uni ? (optionnel)">
          <select value={f.source} onChange={(e) => set("source", e.target.value)} className="inp bg-white" data-testid="adh-source">
            <option value="">— Sélectionnez —</option>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );
}

function StepReview({ f }) {
  const eng = ENGAGEMENT_LABELS[f.engagement_type];
  return (
    <div className="animate-fade-up">
      <div className="eyebrow text-gold-dark">Étape 4 · Confirmation</div>
      <h2 className="font-serif text-2xl sm:text-3xl mt-2 leading-tight text-ink">Récapitulatif de votre demande</h2>
      <p className="mt-3 text-sm text-foreground/65">Vérifiez les informations avant d'envoyer.</p>

      <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-5">
        <ReviewLine label="Nom complet" value={`${f.first_name} ${f.last_name}`} />
        <ReviewLine label="Email" value={f.email} />
        <ReviewLine label="Téléphone" value={f.phone} />
        <ReviewLine label="Pays" value={f.country} />
        <ReviewLine label="Ville" value={f.city} />
        <ReviewLine label="Profession" value={f.profession} />
        <ReviewLine label="Type d'engagement" value={eng?.label || f.engagement_type} highlight />
        {f.source && <ReviewLine label="Source" value={f.source} />}
      </div>

      <div className="mt-8 border-l-4 border-gold pl-5 py-2">
        <div className="eyebrow text-gold-dark">Motivation</div>
        <p className="mt-2 text-foreground/85 italic font-serif leading-relaxed">« {f.motivation} »</p>
      </div>

      <div className="mt-8 p-4 bg-bone rounded-sm text-xs text-foreground/70">
        En cliquant sur <strong>« Envoyer ma demande »</strong>, vous certifiez l'exactitude des informations fournies.
        Votre demande sera examinée par notre équipe et vous recevrez votre numéro officiel <span className="font-mono font-semibold text-ink">FURDC-XXXXX</span> par email dès validation.
      </div>
    </div>
  );
}

function ReviewLine({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[0.65rem] uppercase tracking-widest font-semibold text-foreground/50">{label}</div>
      <div className={`mt-1 ${highlight ? "font-semibold text-ink text-lg" : "text-ink"}`}>{value || <span className="text-foreground/40 italic">—</span>}</div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

/* ============ SUCCÈS ============ */
function SuccessPage({ data }) {
  return (
    <div className="min-h-screen bg-bone flex items-center justify-center py-16 px-6" data-testid="adhesion-success">
      <div className="max-w-2xl w-full bg-white rounded-sm shadow-2xl overflow-hidden animate-scene-in">
        <div className="bg-gradient-to-br from-ink via-ink to-ink text-white p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.22),transparent_60%)]" />
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gold text-ink grid place-items-center mx-auto animate-pulse-soft">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl mt-6 leading-tight">
              Merci <span className="text-gold italic font-light">{data.first_name} !</span>
            </h1>
            <p className="mt-3 text-white/80 max-w-md mx-auto">
              Votre demande d'adhésion au Front Uni pour la RDC a bien été reçue.
            </p>
          </div>
        </div>
        <div className="p-8 sm:p-10">
          <div className="text-center">
            <div className="eyebrow text-gold-dark">Prochaines étapes</div>
            <div className="mt-4 space-y-3 text-left">
              <StepRow n="1" title="Examen de votre demande">
                Notre équipe vérifiera vos informations dans les prochains jours.
              </StepRow>
              <StepRow n="2" title="Attribution de votre numéro officiel">
                Une fois validée, vous recevrez votre <strong className="font-mono">FURDC-XXXXX</strong> par email.
              </StepRow>
              <StepRow n="3" title="Bienvenue au sein du mouvement">
                Vous recevrez toutes les informations pour suivre nos actions et rester engagé(e).
              </StepRow>
            </div>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="btn-primary text-sm" data-testid="adh-success-home">
              Retour à l'accueil
            </Link>
            <Link to="/actualites" className="btn-outline text-sm">Découvrir nos actualités <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepRow({ n, title, children }) {
  return (
    <div className="flex gap-4 items-start bg-bone rounded-sm p-4">
      <div className="w-8 h-8 rounded-full bg-ink text-gold grid place-items-center font-serif shrink-0">{n}</div>
      <div>
        <div className="font-semibold text-ink">{title}</div>
        <div className="text-sm text-foreground/70 mt-0.5">{children}</div>
      </div>
    </div>
  );
}
